import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { Button, Anchor, Tooltip } from '@components/common';
import { cn } from '@utils/cn';

// Interface
// ************************************
export interface JobLocationsProps {
  locations?: ApiSchema<'JobLocationRead'>[];
  partialRemote?: boolean;
  remote?: boolean;
  asLinks?: boolean;
  withTooltip?: boolean;
  displayIcon?: boolean;
  maxDisplayedLocations?: number;
  emphasizeRemote?: boolean;
  locale?: Locale;
  className?: string;
  iconClassName?: string;
  jobSlug?: string; // Job slug to pass to map modal for auto-opening
}

const composeLocationItemClasses = (index: number, maxDisplayed: number, showAll: boolean) => {
  if (index < maxDisplayed) {
    return '';
  }

  return !showAll ? 'hidden' : 'animate-appear';
};

/**
 * @description Job Locations component. Renders locations for a job and also displays info if the job is remote or partial remote.
 * We can set a limit of maximum displayed locations. Note that the locations are only hidden from the UI, they need be rendered in DOM for SEO purposes.
 *
 * - **locations** - JobLocation[] - The locations for the current job.
 * - **partialRemote** - boolean - Whether the job is partial remote.
 * - **remote** - boolean - Whether the job is full remote. If true, there are no locations.
 * - **asLinks** - boolean. Whether to display as links or not.
 * - **withTooltip** - boolean. Whether to display the tooltip or not.
 * - **displayIcon** - boolean. Whether to display the MapPin icon at the beginning of the component.
 * - **maxDisplayedLocations** - number. Maximum displayed locations.
 * - **emphasizeRemote** - boolean. Whether to emphasize Remote labels or not.
 * - **locale** - Locale. The current app locale.
 * - **className** - string. Additional classnames to add to the parent div.
 * - **iconClassName** - string. Additional classnames to add to the icon.
 */
export const JobLocations = (props: JobLocationsProps) => {
  // Destructure props
  const {
    locations,
    partialRemote,
    remote,
    asLinks = true,
    withTooltip = false,
    displayIcon = true,
    maxDisplayedLocations = Infinity,
    emphasizeRemote = true,
    locale,
    className = '',
    iconClassName = '',
    jobSlug,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Show all locations state
  const [showAll, setShowAll] = useState<boolean>(false);

  // When locations are updated (eg - a new job is displayed), reset the show all
  useEffect(() => {
    setShowAll(false);
  }, [locations]);

  // Return null if locations are empty and job is not remote
  if (!locations?.length && !remote) {
    return null;
  }

  // Helper variables
  // ************************************
  const showMoreButton = !!locations?.length && locations.length > maxDisplayedLocations && !showAll;
  const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

  const handleOpenMapModal = (location: ApiSchema<'JobLocationRead'>) => {
    if (!googleApiKey || !locale) return;
    const lat = location.geoLocation?.lat;
    const lng = location.geoLocation?.lng;

    if (typeof lat !== 'number' || typeof lng !== 'number') return;

    void (async () => {
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({
        googleApiKey,
        locale,
        initialCenter: { lat, lng },
        initialZoom: 15,
        jobSlug,
      });
    })();
  };

  // Render location name
  // ************************************
  const renderLocationName = (loc: ApiSchema<'JobLocationRead'>) => {
    const label = loc.name;
    if (loc.withFullAddress) {
      return (
        <Button type="button" styling="none" className="text-left hover:text-ink" onClick={() => handleOpenMapModal(loc)}>
          {label}
        </Button>
      );
    }
    if (asLinks) {
      return (
        <Anchor
          styling="none"
          href="app_job_canonical_city"
          symfonyRoute
          symfonyParams={{ location: loc.slug }}
          className="hover:text-ink"
        >
          {label}
        </Anchor>
      );
    }
    return label;
  };

  // Render component
  // ************************************
  return (
    <div className={cn('items-start flex', className)}>
      {displayIcon && <MapPinIcon className={`mr-2 mt-px size-4 shrink-0 ${iconClassName}`} />}
      <div className="flex-1 text-left">
        {/* Locations */}
        {!!locations?.length && locations.map((location, index) => {
          const shouldShowTooltip = Boolean(withTooltip && location.tooltip && !location.withFullAddress);

          // Two addresses in the same city share the city slug, so the slug alone isn't a unique key.
          return (
            <span className={composeLocationItemClasses(index, maxDisplayedLocations, showAll)} key={`${location.slug}-${index}`}>
              {shouldShowTooltip ? (
                <Tooltip tooltipText={location.tooltip ?? ''} colorScheme="black" position="top" floating>
                  {renderLocationName(location)}
                </Tooltip>
              ) : renderLocationName(location)}
              {index < locations.length - 1 && (index < maxDisplayedLocations || showAll) && ', '}
            </span>
          );
        })}

        {/* Show all locations */
          showMoreButton && (
            <Button type="button" styling="none" className="hover:text-ink" onClick={() => setShowAll(true)}>
              ...
            </Button>
          )
        }

        {/* Partial remote */
          partialRemote && (
            <>
              {!showMoreButton && ';'}
              <span className={emphasizeRemote ? 'font-bold' : ''}>
                {` ${t('job.add.label.partial.remote')}`}
              </span>
            </>
          )
        }

        {/* Remote */
          remote && (
            <span className={emphasizeRemote ? 'font-bold' : ''}>{t('job.add.label.remote')}</span>
          )
        }
      </div>
    </div>
  );
};
